
import {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {agentApi, aiSiteApi, customersApi, sitesApi} from '../lib/api-resources';
import type {AgentContext, AgentPreviewResponse, AgentPublishResponse, AgentVersionResponse, BriefResponse, Customer, GeneratePlanResponse, Site, SiteJobStatusResponse, SitePlan, VisualCriticReport} from '../lib/types';
import './AgentPage.css';

const DEFAULT_COMMAND = '用现有企业资料和商品重新设计官网。面向欧美批发采购商，英文为主，同时支持西班牙语和法语。风格高级、时尚，不要传统外贸网站。';
const errText = (value: unknown) => {
  const raw = value instanceof Error ? value.message : String(value || '');
  if (/token configured|WP side|site agent|model router|AI generation/i.test(raw)) {
    return '网站生成服务暂时未准备好。当前线上网站不会改变，请联系管理员完成生成服务配置后重试。';
  }
  return raw || '操作没有完成，请稍后重试。';
};
const stateText = (value: AgentContext['readiness'][number]['state']) => ({ready: '已具备', missing: '需要补充', problem: '需要处理', confirm: '待确认'}[value]);
const humanGoal = (value: string | null | undefined) => ({
  generate_leads: '获取询盘',
  showcase_products: '展示产品',
  build_trust: '建立信任',
  inform_visitors: '帮助访客了解公司',
  drive_signups: '获取订阅',
  support_customers: '服务现有客户',
} as Record<string, string>)[value || ''] || value || '展示产品并获取询盘';
const humanLanguage = (value: string | null | undefined) => ({
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  zh: '中文',
} as Record<string, string>)[(value || '').toLowerCase()] || value || '未设置';
const sectionLabel = (value: string) => ({
  hero: '首屏',
  features: '优势与卖点',
  about: '公司介绍',
  products: '产品展示',
  cta: '询盘入口',
  faq: '常见问题',
} as Record<string, string>)[value] || value;
const jobStatusLabel = (value: string) => ({
  SUCCESS: '已完成',
  PARTIAL: '部分完成，需要重试',
  FAILED: '生成失败',
  CANCELLED: '已取消',
} as Record<string, string>)[value] || '处理中';
const visualRoleLabel = (value: string) => ({
  HERO_IMAGE: '首页主图',
  ABOUT_IMAGE: '公司介绍图片',
  SECTION_IMAGE: '页面配图',
  PRODUCT_IMAGE: '产品图片',
  CATEGORY_IMAGE: '分类图片',
  ASSET: '网站图片',
} as Record<string, string>)[value] || '网站图片';
const visualStatusLabel = (value: string) => ({
  PLANNED: '待准备',
  PENDING: '待准备',
  GENERATING: '准备中',
  READY: '已准备',
  APPROVED: '已确认',
  FAILED: '需要重试',
} as Record<string, string>)[value] || '待准备';
const findingSeverityLabel = (value: string) => ({
  BLOCKER: '需要处理',
  REQUIRED: '需要处理',
  RECOMMENDED: '建议优化',
  OPTIONAL: '可选优化',
} as Record<string, string>)[value] || '需要关注';
const visualFindingLabel = (value: string) => ({
  HERO_MISSING: '首页主图还没有准备好。',
  QUALITY_POOR: '部分商品还缺少主图。',
  DETAIL_INSUFFICIENT: '部分商品还缺少细节图。',
  LIFESTYLE_MISSING: '部分商品还缺少使用场景图。',
  MODEL_MISSING: '还没有准备佩戴或场景图片。',
} as Record<string, string>)[value] || '有些网站图片还需要补充。';
const mediaAuditSummary = (audit: Record<string, unknown>) => {
  const productCount = Array.isArray(audit.product_audits) ? audit.product_audits.length : 0;
  const imageCount = Number(audit.total_images_analyzed || 0);
  const findingCount = Array.isArray(audit.findings) ? audit.findings.length : 0;
  if (!productCount && !imageCount) return '还没有商品图片。导入货盘后，系统会检查产品图片是否完整。';
  return `已检查 ${productCount} 个商品和 ${imageCount} 张图片，发现 ${findingCount} 项图片准备建议。`;
};

function emptyAgentBrief(): NonNullable<BriefResponse['brief']> {
  return {
    company_name: '', brand_name: '', industry: '', company_description: '',
    products_services: '', target_customers: '', markets: '', countries: '',
    competitive_advantages: '', certifications: '', years_in_business: null,
    contact_email: null, contact_phone: '', address: '', primary_cta_url: '',
    languages: [], preferred_tone: 'professional', primary_goal: 'generate_leads',
    reference_notes: '', raw_materials: '',
  };
}

function DesignBriefCard({brief}: {brief: AgentContext['design_brief']}) {
  return <div className="agent-brief-card"><div className="agent-panel-heading"><div><div className="agent-card-kicker">设计方向</div><h2>Agent 对这次网站的理解</h2></div><span className="agent-source-note">结构化，可追溯</span></div><div className="agent-brief-lead"><strong>{brief.brand_positioning}</strong><span>{brief.target_audience} · {humanGoal(brief.commercial_goal)}</span></div><div className="agent-brief-grid"><div><strong>视觉方向</strong><p>{brief.visual_direction}</p></div><div><strong>图片方向</strong><p>{brief.image_direction}</p></div><div><strong>版式原则</strong><ul>{brief.layout_principles.map((item) => <li key={item}>{item}</li>)}</ul></div><div><strong>转化策略</strong><p>{brief.conversion_strategy}</p></div></div><div className="agent-avoid-list"><strong>明确避免</strong>{brief.avoid_list.map((item) => <span key={item}>{item}</span>)}</div></div>;
}

export default function AgentPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [context, setContext] = useState<AgentContext | null>(null);
  const [command, setCommand] = useState(DEFAULT_COMMAND);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    customersApi.list({page: 1, page_size: 200, status: 'ACTIVE'}).then((response) => {
      if (!alive) return;
      setCustomers(response.items || []);
      const preferred = response.items?.[0];
      if (preferred) setCustomerId(preferred.id);
    }).catch((reason) => alive && setError(errText(reason)));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!customerId) return;
    let alive = true;
    sitesApi.list({page: 1, page_size: 200, customer_id: customerId}).then((response) => {
      if (!alive) return;
      setSites(response.items || []);
      const preferred = response.items?.[0];
      setSiteId(preferred?.id || '');
    }).catch((reason) => alive && setError(errText(reason)));
    return () => { alive = false; };
  }, [customerId]);

  useEffect(() => {
    if (!siteId) return;
    let alive = true;
    agentApi.context(siteId).then((value) => alive && setContext(value)).catch((reason) => alive && setError(errText(reason)));
    return () => { alive = false; };
  }, [siteId]);

  async function start() {
    if (!siteId || !command.trim()) {
      setError('请先选择客户和网站，再告诉 Agent 你想做什么。');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await agentApi.command(siteId, {command, action: 'prepare_redesign'});
      setContext(response.context);
      setMessage(response.message);
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="agent-page page">
      <header className="agent-header"><div><div className="agent-eyebrow">建站助手</div><h1>您好，我是 WJ 建站助手</h1><p>今天想为哪个客户做什么？我会先读取已有资料，再告诉你缺什么、准备执行什么。</p></div><div className="agent-header-actions"><Link className="agent-secondary-link" to="/customers">新建客户</Link><Link className="agent-secondary-link" to="/sites">新建网站</Link><Link className="agent-secondary-link" to="/settings">高级设置</Link></div></header>
      {error && <div className="agent-alert">{error}</div>}
      <section className="agent-command-layout">
        <div className="agent-command-card"><div className="agent-card-kicker">自然语言入口</div><h2>把目标告诉我，剩下的我来拆解</h2><textarea value={command} onChange={(event) => setCommand(event.target.value)} rows={5} aria-label="告诉 Site Agent 你想做什么" /><div className="agent-command-foot"><div className="agent-suggestion-row"><button type="button" onClick={() => setCommand('重新设计这个网站，突出真实商品和批发询盘。')}>重新设计网站</button><button type="button" onClick={() => setCommand('检查现在的网站还缺什么。')}>检查资料</button></div><button className="agent-primary-button" type="button" onClick={start} disabled={busy}>{busy ? '正在理解…' : '让 Agent 开始'}</button></div>{message && <div className="agent-response">✦ {message}</div>}</div>
        <div className="agent-selection-card"><div className="agent-card-kicker">当前工作对象</div><label>客户<select value={customerId} onChange={(event) => setCustomerId(event.target.value)}><option value="">选择客户</option>{customers.map((item) => <option key={item.id} value={item.id}>{item.company_name}</option>)}</select></label><label>网站<select value={siteId} onChange={(event) => setSiteId(event.target.value)}><option value="">选择网站</option>{sites.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><div className="agent-create-links"><Link to="/customers">客户不存在？新建客户</Link><Link to="/sites">为客户新建网站</Link></div>{context && <div className="agent-selection-summary"><strong>{context.customer.name}</strong><span>{context.site.name}</span><span>{context.site.domain || '域名待设置'}</span></div>}</div>
      </section>
      {context && <><section className="agent-context-overview"><div><div className="agent-card-kicker">Agent 已读懂</div><h2>{context.summary.headline}</h2><p>目标是 <strong>{humanGoal(context.summary.goal)}</strong>，主语言为 <strong>{humanLanguage(context.summary.language)}</strong>，当前使用 <strong>{context.summary.hosting}</strong>。</p><button className="agent-primary-button" type="button" onClick={() => navigate('/agent/sites/' + siteId)}>进入建站工作台 →</button></div><div className="agent-overview-stats"><div><strong>{context.readiness.filter((item) => item.state === 'ready').length}</strong><span>项已具备</span></div><div><strong>{context.readiness.filter((item) => item.state === 'missing' || item.state === 'problem').length}</strong><span>项待处理</span></div><div><strong>{context.catalog.product_count ?? '—'}</strong><span>个商品</span></div><div><strong>{context.catalog.media_count ?? '—'}</strong><span>张图片</span></div></div></section><section className="agent-section"><div className="agent-section-heading"><div><div className="agent-card-kicker">资料准备情况</div><h2>继续之前，先看清楚系统已经知道什么</h2></div><span className="agent-source-note">来源：客户、网站、商品、媒体和历史版本</span></div><div className="agent-readiness-grid">{context.readiness.map((item) => <div className={'agent-readiness-card state-' + item.state} key={item.key}><div className="agent-readiness-top"><span className="agent-readiness-dot" />{stateText(item.state)}</div><strong>{item.label}</strong><p>{item.summary}</p>{item.action && <small>{item.action} →</small>}</div>)}</div></section><section className="agent-lower-grid"><div className="agent-panel"><div className="agent-panel-heading"><div><div className="agent-card-kicker">已发生的动作</div><h2>每一步都有记录</h2></div><span className="agent-provenance-badge">操作记录</span></div>{context.activity.length === 0 ? <p className="agent-muted">还没有 Agent 操作。</p> : context.activity.slice(0, 5).map((item) => <div className="agent-activity-row" key={item.id}><span>✓</span><div><strong>{item.label}</strong><small>{item.message}</small></div><time>{item.created_at || '刚刚'}</time></div>)}</div><div className="agent-panel"><div className="agent-card-kicker">快捷操作</div><h2>下一步可以做什么</h2><div className="agent-quick-actions">{context.actions.slice(0, 6).map((item) => <button type="button" key={item.key} onClick={() => item.key === 'redesign' ? navigate('/agent/sites/' + siteId) : setCommand(item.label)}>{item.label}<span>→</span></button>)}</div></div></section></>}
    </div>
  );
}

export function AgentWorkspacePage() {
  const {siteId} = useParams<{siteId: string}>();
  const [context, setContext] = useState<AgentContext | null>(null);
  const [briefResponse, setBriefResponse] = useState<BriefResponse | null>(null);
  const [brief, setBrief] = useState(emptyAgentBrief());
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefSaving, setBriefSaving] = useState(false);
  const [command, setCommand] = useState(DEFAULT_COMMAND);
  const [planResponse, setPlanResponse] = useState<GeneratePlanResponse | null>(null);
  const [plan, setPlan] = useState<SitePlan | null>(null);
  const [job, setJob] = useState<SiteJobStatusResponse | null>(null);
  const [report, setReport] = useState<VisualCriticReport | null>(null);
  const [version, setVersion] = useState<AgentVersionResponse | null>(null);
  const [preview, setPreview] = useState<AgentPreviewResponse | null>(null);
  const [published, setPublished] = useState<AgentPublishResponse | null>(null);
  const [approved, setApproved] = useState(false);
  const [modify, setModify] = useState('缩小首屏标题，突出发饰和批发询盘。');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!siteId) return;
    let alive = true;
    Promise.all([
      agentApi.context(siteId),
      aiSiteApi.lastPlan(siteId).catch(() => null),
      aiSiteApi.getBrief(siteId).catch(() => null),
      aiSiteApi.listJobs(siteId).catch(() => null),
    ]).then(async ([nextContext, previous, nextBrief, jobs]) => {
      if (!alive) return;
      setContext(nextContext);
      setApproved(Boolean(nextContext.draft.approval?.approved || nextContext.draft.has_approval));
      const savedVersion = nextContext.draft.latest_saved_version || nextContext.draft.current_version;
      setVersion(savedVersion ? {
        version_id: savedVersion.version_id,
        label: savedVersion.label,
        draft_revision: savedVersion.draft_revision,
        created_at: savedVersion.created_at,
        provenance: {},
      } : null);
      const savedPreview = nextContext.draft.preview;
      if (savedPreview?.release_id) {
        setPreview({
          site_id: nextContext.site_id,
          release_id: savedPreview.release_id,
          artifact_id: savedPreview.artifact_id,
          version: savedPreview.version,
          draft_revision: nextContext.draft.draft_revision,
          product_count: savedPreview.product_count,
          media_count: savedPreview.media_count,
          payload_sha256: '',
          package_sha256: '',
          preview_passed: savedPreview.preview_passed,
          user_confirmed: savedPreview.user_confirmed,
          preview_url: savedPreview.preview_url,
          message: savedPreview.user_confirmed ? '预览已确认，可以发布。' : '请打开预览确认页面。',
        });
      } else setPreview(null);
      const savedRelease = nextContext.draft.published_release;
      setPublished(savedRelease && nextContext.draft.published ? {
        site_id: nextContext.site_id,
        release_id: savedRelease.release_id,
        artifact_id: savedRelease.artifact_id,
        version: savedRelease.version,
        status: 'published',
        public_url: savedRelease.public_url || nextContext.site.url || '',
        rollback_ready: savedRelease.rollback_ready,
        message: '网站已发布。',
        direct_job_id: savedRelease.direct_job_id,
      } : null);
      if (nextBrief) {
        setBriefResponse(nextBrief);
        setBrief({...emptyAgentBrief(), ...nextBrief.brief});
        setBriefOpen(nextBrief.version === 0 || nextBrief.status !== 'CONFIRMED');
      }
      if (previous && previous.status === 'SUCCESS' && previous.plan) {
        setPlanResponse(previous);
        setPlan(previous.plan);
      }
      const latestGeneration = jobs?.items?.find((item) => item.job_kind === 'SITE_GENERATE');
      if (latestGeneration) {
        const restoredJob = await aiSiteApi.getJob(siteId, latestGeneration.job_id);
        if (!alive) return;
        setJob(restoredJob);
        setReport(await agentApi.visualCritic(siteId, {viewport: 'agent-workbench'}));
      }
    }).catch((reason) => alive && setError(errText(reason)));
    return () => { alive = false; };
  }, [siteId]);

  async function refresh() {
    if (siteId) setContext(await agentApi.context(siteId));
  }

  function updateBrief<K extends keyof ReturnType<typeof emptyAgentBrief>>(key: K, value: ReturnType<typeof emptyAgentBrief>[K]) {
    setBrief((current) => ({...current, [key]: value}));
  }

  const briefComplete = Boolean(
    brief.company_description?.trim() &&
    brief.products_services?.trim() &&
    brief.target_customers?.trim(),
  );

  async function saveBrief() {
    if (!siteId || !briefComplete) {
      setError('请至少补充公司简介、主营商品和目标客户，Agent 才能生成可靠方案。');
      setBriefOpen(true);
      return;
    }
    setBriefSaving(true);
    setError('');
    try {
      const saved = await aiSiteApi.upsertBrief(siteId, {
        expected_version: briefResponse?.version ?? 0,
        brief: {...brief, contact_email: brief.contact_email?.trim() || null, languages: []},
      });
      setBriefResponse(saved);
      setBrief({...emptyAgentBrief(), ...saved.brief});
      setBriefOpen(false);
      await refresh();
      setNotice('企业资料已确认。语言仍由网站设置统一管理。');
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBriefSaving(false);
    }
  }

  async function makePlan() {
    if (!siteId) return;
    if (!briefResponse || briefResponse.version === 0 || !briefComplete) {
      setBriefOpen(true);
      setError('先确认企业资料，再生成网站方案。语言无需重复填写。');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const recorded = await agentApi.command(siteId, {command, action: 'design_brief'});
      setContext(recorded.context);
      const response = await aiSiteApi.generatePlan(siteId, {operator_notes: command, idempotency_key: 'agent-plan-' + Date.now(), workflow: 'agent'});
      setPlanResponse(response);
      setPlan(response.plan);
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function makeDraft() {
    if (!siteId || !context || !planResponse?.job_id || !plan) return;
    setBusy(true);
    setError('');
    try {
      const confirmed = await aiSiteApi.confirmPlan(siteId, {expected_plan_job_id: planResponse.job_id, expected_draft_revision: context.draft.draft_revision, plan, workflow: 'agent'});
      const created = await aiSiteApi.generateFullSite(siteId, {expected_plan_job_id: planResponse.job_id, expected_draft_revision: confirmed.draft_revision, idempotency_key: 'agent-generate-' + Date.now(), human_modified_policy: 'protect', workflow: 'agent'});
      let current = await aiSiteApi.getJob(siteId, created.job_id);
      setJob(current);
      for (let attempt = 0; attempt < 120 && !['SUCCESS', 'PARTIAL', 'FAILED', 'CANCELLED'].includes(current.status); attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
        current = await aiSiteApi.getJob(siteId, created.job_id);
        setJob(current);
      }
      await refresh();
      setReport(await agentApi.visualCritic(siteId, {viewport: 'agent-workbench'}));
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function retryFailed() {
    if (!siteId || !job) return;
    setBusy(true);
    setError('');
    try {
      const created = await aiSiteApi.retryJob(siteId, job.job_id);
      let current = await aiSiteApi.getJob(siteId, created.job_id);
      setJob(current);
      for (let attempt = 0; attempt < 120 && !['SUCCESS', 'PARTIAL', 'FAILED', 'CANCELLED'].includes(current.status); attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
        current = await aiSiteApi.getJob(siteId, created.job_id);
        setJob(current);
      }
      await refresh();
      setReport(await agentApi.visualCritic(siteId, {viewport: 'agent-workbench'}));
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function critic() {
    if (!siteId) return;
    setBusy(true);
    try {
      setReport(await agentApi.visualCritic(siteId, {viewport: 'agent-workbench'}));
      await refresh();
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function modifyDraft() {
    if (!siteId || !context) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await agentApi.modify(siteId, {command: modify, expected_revision: context.draft.draft_revision});
      if (!result.modified) setError(result.message);
      else {
        setNotice(`${result.message}${result.changes.length ? ' 修改内容：' + result.changes.join('；') + '。' : ''}`);
        await refresh();
        setReport(await agentApi.visualCritic(siteId, {viewport: 'agent-workbench'}));
      }
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function saveVersion() {
    if (!siteId) return;
    setBusy(true);
    try {
      setVersion(await agentApi.saveVersion(siteId, {label: 'Agent redesign draft', visual_report_id: report?.report_id}));
      await refresh();
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    if (!siteId || !context || !report) return;
    setBusy(true);
    try {
      await agentApi.approve(siteId, {expected_revision: context.draft.draft_revision, visual_report_id: report.report_id});
      setApproved(true);
      setNotice('版本已批准。请继续打开预览、确认预览，再发布网站。');
      await refresh();
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function createPreview() {
    if (!siteId || !approved) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const created = await agentApi.preview(siteId);
      setPreview(created);
      setNotice(created.message);
      await refresh();
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function confirmPreview() {
    if (!siteId || !context || !preview) return;
    setBusy(true);
    setError('');
    try {
      const confirmed = await agentApi.approvePreview(siteId, {
        release_id: preview.release_id,
        expected_revision: context.draft.draft_revision,
      });
      setPreview(confirmed);
      setNotice(confirmed.message);
      await refresh();
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  async function publishDraft() {
    if (!siteId || !preview?.user_confirmed) return;
    setBusy(true);
    setError('');
    try {
      const result = await agentApi.publish(siteId, {release_id: preview.release_id});
      setPublished(result);
      setNotice(result.message);
      await refresh();
    } catch (reason) {
      setError(errText(reason));
    } finally {
      setBusy(false);
    }
  }

  if (!siteId) return <div className="page">缺少网站信息。</div>;
  const step = !plan ? 1 : !job ? 2 : !report ? 3 : approved ? 5 : 4;

  return (
    <div className="agent-page page">
      <header className="agent-workspace-header"><div><Link className="agent-back-link" to="/">← 返回 Agent</Link><div className="agent-eyebrow">建站工作台</div><h1>{context?.site.name || '正在加载网站…'}</h1><p>{context?.customer.name || '正在读取客户'} · 这次操作先进入草稿，不会突然改变线上网站。</p></div></header>
      {error && <div className="agent-alert">{error}</div>}{notice && <div className="agent-success">{notice}</div>}
      <div className="agent-stepper">{[['理解资料', '读取上下文'], ['网站方案', '确认结构'], ['生成草稿', '真实生成记录'], ['视觉审查', '视觉检查'], ['版本批准', '保存并批准']].map((item, index) => <div className={'agent-step ' + (index + 1 <= step ? 'is-active' : '')} key={item[0]}><span>{index + 1}</span><div><strong>{item[0]}</strong><small>{item[1]}</small></div></div>)}</div>
      <section className="agent-workspace-grid"><main className="agent-main-column">
        {/* Capability / Architecture Decision */}
        {context?.capability_decision && (
          <div className="agent-panel agent-decision-panel">
            <div className="agent-decision-header">
              <div>
                <div className="agent-card-kicker">建站方式</div>
                <h2>系统已为这个网站选择合适的建站路径</h2>
              </div>
              <span className="agent-decision-badge">已自动选择</span>
            </div>
            <p className="agent-form-intro">系统会结合网站目标、企业资料和商品资料安排后续步骤。你会先看到预览，确认后才会发布。</p>
            <ul className="agent-benefits-list"><li>✓ 优先使用已确认的企业和商品资料</li><li>✓ 每一步都有可查看的结果</li></ul>
          </div>
        )}

        <div className="agent-panel"><div className="agent-card-kicker">你的意图</div><h2>用自然语言告诉 Agent 设计方向</h2><textarea value={command} onChange={(event) => setCommand(event.target.value)} rows={4} /><button className="agent-primary-button" type="button" onClick={makePlan} disabled={busy}>{busy && !plan ? '正在生成方案…' : '读取资料并生成方案'}</button></div>
        
        {/* Media Audit & Visual Production Plan */}
        {context?.media_audit && (
          <div className="agent-panel agent-media-audit-panel">
            <div className="agent-audit-score-row">
              <div>
                <div className="agent-card-kicker">图片准备</div>
                <h2>检查商品和网站图片是否准备充分</h2>
              </div>
              <div className="agent-audit-score">
                {String(context.media_audit.visual_readiness_score || 0)}<small style={{fontSize: '14px', color: '#8aa2c0'}}>/10</small>
              </div>
            </div>
            <p className="agent-form-intro">{mediaAuditSummary(context.media_audit)}</p>
            
            {Array.isArray(context.media_audit.findings) && (context.media_audit.findings.length > 0) && (
              <div className="agent-findings-list">
                {(context.media_audit.findings as Array<Record<string, unknown>>).slice(0, 4).map((f, i) => (
                  <div key={i} className={`agent-finding-item ${String(f.severity || 'OPTIONAL')}`}>
                    <div><strong>{visualFindingLabel(String(f.finding_type || ''))}</strong></div>
                    <span style={{fontSize: '11px', color: '#7eb1f7'}}>{findingSeverityLabel(String(f.severity || ''))}</span>
                  </div>
                ))}
              </div>
            )}

            {context?.visual_plan && (
              <div style={{marginTop: '16px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <strong style={{color: '#e4efff', fontSize: '13px'}}>图片准备计划</strong>
                  <button 
                    className="agent-primary-button" 
                    type="button" 
                    onClick={async () => {
                      if (!siteId) return;
                      setBusy(true);
                      try {
                        const res = await agentApi.executeVisualPlan(siteId);
                        setNotice(`图片素材已准备 (${res.ready_count} 张可使用)`);
                        await refresh();
                      } catch (e) {
                        setError(errText(e));
                      } finally {
                        setBusy(false);
                      }
                    }} 
                    disabled={busy}
                  >
                    {busy ? '正在准备图片…' : '准备网站图片'}
                  </button>
                </div>
                {Array.isArray((context.visual_plan as Record<string, unknown>).tasks) && (
                  <div className="agent-visual-tasks-grid">
                    {((context.visual_plan as Record<string, unknown>).tasks as Array<Record<string, unknown>>).map((t, idx) => (
                      <div key={idx} className={`agent-visual-task-card ${t.status === 'READY' || t.status === 'APPROVED' ? 'is-ready' : ''}`}>
                        <div style={{display: 'flex', justifyContent: 'space-between'}}>
                          <span className="agent-task-badge">{visualRoleLabel(String(t.role || 'ASSET'))} ({String(t.aspect_ratio || '16:9')})</span>
                          <span style={{fontSize: '10px', color: t.status === 'READY' || t.status === 'APPROVED' ? '#71e4a7' : '#9bb0c7'}}>{visualStatusLabel(String(t.status || 'PLANNED'))}</span>
                        </div>
                        {Boolean(t.output_url) && (
                        <img src={String(t.output_url)} alt="网站图片预览" className="agent-task-preview-img" />
                        )}
                        <p style={{fontSize: '11px', color: '#91a4bc', margin: 0, lineHeight: '1.3'}}>系统会按网站风格准备这类图片，完成后可在这里查看预览。</p>
                        {t.status === 'READY' && Boolean(t.asset_id) && (
                          <button 
                            className="agent-secondary-button" 
                            type="button" 
                            style={{marginTop: 'auto', fontSize: '11px', padding: '4px 8px'}}
                            onClick={async () => {
                              if (!siteId || !t.asset_id) return;
                              setBusy(true);
                              try {
                                await agentApi.approveVisualAsset(siteId, String(t.asset_id));
                                setNotice(`已确认${visualRoleLabel(String(t.role))}，可以用于网站。`);
                                await refresh();
                              } catch (e) {
                                setError(errText(e));
                              } finally {
                                setBusy(false);
                              }
                            }}
                          >
                            批准并绑定到网站
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Curated WordPress Themes Recommendation */}
        {Array.isArray(context?.theme_recommendations) && (context.theme_recommendations.length > 0) && (
          <div className="agent-panel">
            <div className="agent-panel-heading">
              <div>
                <div className="agent-card-kicker">网站风格建议</div>
                <h2>选择一个适合你的网站风格</h2>
              </div>
            </div>
            <p className="agent-form-intro">这里会根据网站目标提供可选的页面风格。你可以先看预览，再决定是否使用。</p>
            <div className="agent-themes-grid">
              {(context.theme_recommendations as Array<Record<string, unknown>>).map((theme) => (
                <div key={String(theme.slug)} className="agent-theme-card">
                  {Boolean(theme.screenshot_url) && (
                    <img src={String(theme.screenshot_url)} alt={String(theme.name)} className="agent-theme-thumb" />
                  )}
                  <div className="agent-theme-info">
                    <h3>{String(theme.name)}</h3>
                    <p>{theme.is_block_theme ? '现代、清晰，适合展示品牌和产品。' : '稳妥、易读，适合产品目录和询盘入口。'}</p>
                    <div style={{marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <span style={{fontSize: '10px', color: '#7eb1f7'}}>{theme.is_block_theme ? '块式布局' : '经典布局'}</span>
                      <button
                        className="agent-secondary-button"
                        type="button"
                        style={{fontSize: '11px', padding: '4px 8px'}}
                        onClick={async () => {
                          if (!siteId) return;
                          setBusy(true);
                          try {
                            await agentApi.selectTheme(siteId, {
                              slug: String(theme.slug),
                              name: String(theme.name || theme.slug),
                              screenshot_url: String(theme.screenshot_url || ''),
                            });
                            setNotice(`已选择主题：${String(theme.name || theme.slug)}`);
                            await refresh();
                          } catch (e) {
                            setError(errText(e));
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        选择此主题
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {context?.design_brief && <DesignBriefCard brief={context.design_brief} />}
        <section className="agent-panel agent-brief-form-panel"><div className="agent-panel-heading"><div><div className="agent-card-kicker">资料确认</div><h2>{briefResponse?.version ? '企业资料已保存，可继续修改' : '先确认这次建站需要的企业资料'}</h2></div><button className="agent-text-button" type="button" onClick={() => setBriefOpen((open) => !open)}>{briefOpen ? '收起' : '展开确认'}</button></div><p className="agent-form-intro">只填写 Agent 需要的事实。没有准备好的内容可以留空，系统不会自动编造认证、客户 Logo、MOQ 或销售数字。</p>{briefOpen && <><div className="agent-form-grid"><label>公司名称<input value={brief.company_name ?? ''} onChange={(event) => updateBrief('company_name', event.target.value)} /></label><label>主营行业<input value={brief.industry ?? ''} onChange={(event) => updateBrief('industry', event.target.value)} placeholder="例如：饰品 / 发饰" /></label><label className="agent-form-wide">公司简介<textarea value={brief.company_description ?? ''} onChange={(event) => updateBrief('company_description', event.target.value)} placeholder="公司在哪里、主要做什么、服务什么类型的客户？" rows={3} /></label><label className="agent-form-wide">主营商品<textarea value={brief.products_services ?? ''} onChange={(event) => updateBrief('products_services', event.target.value)} placeholder="例如：发箍、发圈、发夹；面向海外批发采购" rows={3} /></label><label className="agent-form-wide">目标客户<textarea value={brief.target_customers ?? ''} onChange={(event) => updateBrief('target_customers', event.target.value)} placeholder="例如：海外饰品批发商、采购商和零售渠道" rows={3} /></label><label>主要市场<input value={brief.markets ?? ''} onChange={(event) => updateBrief('markets', event.target.value)} placeholder="例如：欧美、非洲" /></label><label>联系邮箱<input type="email" value={brief.contact_email ?? ''} onChange={(event) => updateBrief('contact_email', event.target.value)} /></label><label>电话 / WhatsApp<input value={brief.contact_phone ?? ''} onChange={(event) => updateBrief('contact_phone', event.target.value)} /></label><label>公司地址<input value={brief.address ?? ''} onChange={(event) => updateBrief('address', event.target.value)} /></label><label className="agent-form-wide">竞争优势<textarea value={brief.competitive_advantages ?? ''} onChange={(event) => updateBrief('competitive_advantages', event.target.value)} placeholder="只写已经确认的优势，例如：义乌供应链、真实产品货盘" rows={2} /></label><label className="agent-form-wide">已确认的资质 / 认证（可选）<textarea value={brief.certifications ?? ''} onChange={(event) => updateBrief('certifications', event.target.value)} placeholder="没有就留空" rows={2} /></label><label>内容语气<select value={brief.preferred_tone ?? 'professional'} onChange={(event) => updateBrief('preferred_tone', event.target.value as NonNullable<typeof brief.preferred_tone>)}><option value="professional">专业清晰</option><option value="friendly">友好自然</option><option value="minimal">简洁克制</option><option value="authoritative">专业权威</option><option value="warm">温和亲切</option></select></label><label>网站主要目标<select value={brief.primary_goal ?? 'generate_leads'} onChange={(event) => updateBrief('primary_goal', event.target.value as NonNullable<typeof brief.primary_goal>)}><option value="generate_leads">获取询盘</option><option value="showcase_products">展示产品</option><option value="build_trust">建立信任</option></select></label><label className="agent-form-wide">主按钮链接<input value={brief.primary_cta_url ?? ''} onChange={(event) => updateBrief('primary_cta_url', event.target.value)} placeholder="例如 /inquiry/；不确定可留空" /></label></div><div className="agent-language-readonly"><strong>网站语言</strong><span>{context?.site.languages.map(humanLanguage).join(' · ') || '尚未设置'}</span><Link to="/sites">到网站设置修改语言</Link><small>语言属于网站全局配置，AI 建站不会再次要求填写。</small></div><div className="agent-form-actions"><span>{briefComplete ? '必需资料已具备' : '还需要公司简介、主营商品和目标客户'}</span><button className="agent-primary-button" type="button" onClick={saveBrief} disabled={briefSaving}>{briefSaving ? '正在保存…' : '确认并保存企业资料'}</button></div></>}</section>
        {plan && <div className="agent-panel"><div className="agent-panel-heading"><div><div className="agent-card-kicker">设计方向 → 网站方案</div><h2>这是 Agent 准备执行的方案</h2></div><span className="agent-plan-badge">{plan.recommended_pages.length} 个页面</span></div><p>{plan.content_strategy || '以真实企业资料、商品货盘和询盘目标为内容主轴。'}</p><div className="agent-plan-pages">{plan.recommended_pages.map((page) => <article className="agent-plan-page" key={page.role + page.slug}><strong>{page.title}</strong><span>/{page.slug}</span><p>{page.notes || '由 Agent 根据真实资料生成。'}</p><div>{page.sections.map((section) => <em key={page.slug + section.type}>{sectionLabel(section.type)}</em>)}</div></article>)}</div><button className="agent-primary-button" type="button" onClick={makeDraft} disabled={busy || Boolean(job)}>{job ? '草稿已生成' : busy ? '正在生成草稿…' : '确认方案并开始生成'}</button></div>}
        {job && <div className="agent-panel"><div className="agent-card-kicker">真实执行记录</div><h2>Agent 生成了什么</h2><span className="agent-job-badge">{jobStatusLabel(job.status)}</span><div className="agent-job-summary">{job.sections.map((item) => <div className="agent-job-row" key={item.id}><span className={'job-dot job-' + item.status.toLowerCase()} /><strong>{sectionLabel(item.type)}</strong><span>{item.status === 'SUCCESS' ? '已完成' : item.status === 'FAILED' ? '需要重试' : '处理中'}</span></div>)}</div>{job.sections.some((item) => item.status === 'FAILED') && <><p className="agent-form-intro">只有失败的区块会重试，已完成内容不会被覆盖。</p><button className="agent-secondary-button" type="button" onClick={retryFailed} disabled={busy}>重试失败部分</button></>}</div>}
        {report && <div className={'agent-panel agent-critic-panel ' + (report.gate_passed ? 'critic-pass' : 'critic-fail')}><div className="agent-panel-heading"><div><div className="agent-card-kicker">视觉检查</div><h2>{report.gate_passed ? '视觉审查通过' : '视觉审查未通过，不能发布'}</h2></div><strong className="agent-overall-score">{report.overall}<small>/10</small></strong></div><div className="agent-score-grid">{report.scores.map((item) => <div className="agent-score-row" key={item.key}><span>{item.label}</span><div className="agent-score-bar"><i style={{width: item.score * 10 + '%'}} /></div><strong>{item.score}</strong></div>)}</div><p>{report.looks_like_ai_template ? '仍有明显 AI 模板感，需要继续修改。' : '没有检测到明显 AI 模板感。'}</p>{report.issues.map((issue) => <div className="agent-issue" key={issue}>{issue}</div>)}<button className="agent-secondary-button" type="button" onClick={critic} disabled={busy}>重新审查</button></div>}
        {context?.draft.has_draft && <div className="agent-panel"><div className="agent-card-kicker">自然语言修改</div><h2>哪里不对，直接告诉 Agent</h2><div className="agent-modify-row"><input value={modify} onChange={(event) => setModify(event.target.value)} /><button className="agent-primary-button" type="button" onClick={modifyDraft} disabled={busy}>修改草稿</button></div><p>修改只作用于 Draft，完成后会自动重新审查。</p></div>}
      </main><aside className="agent-side-column">
        <div className="agent-panel"><div className="agent-card-kicker">已具备 / 仍需确认</div><h2>系统知道什么</h2>{context?.readiness.map((item) => <div className="agent-context-line" key={item.key}><span className={'context-state context-' + item.state}>{item.state === 'ready' ? '✓' : '!'}</span><div><strong>{item.label}</strong><small>{item.summary}</small></div></div>)}</div>
        <div className="agent-panel"><div className="agent-card-kicker">版本与发布</div><h2>由你确认最后一步</h2><div className="agent-release-check"><span className={version ? 'check-done' : ''}>1</span><div><strong>保存版本</strong><small>{version ? '已保存当前版本' : '保存一份可恢复版本'}</small></div></div><button className="agent-secondary-button agent-full-button" onClick={saveVersion} disabled={busy || !context?.draft.has_draft}>保存当前版本</button><div className="agent-release-check"><span className={approved ? 'check-done' : ''}>2</span><div><strong>批准版本</strong><small>{approved ? '已批准' : '视觉检查通过后才能批准'}</small></div></div><button className="agent-primary-button agent-full-button" onClick={approve} disabled={busy || !report?.gate_passed || approved}>{approved ? '已批准' : '批准这个版本'}</button><div className="agent-release-check"><span className={preview?.preview_passed ? 'check-done' : ''}>3</span><div><strong>生成发布前预览</strong><small>{preview ? `版本 v${preview.version} · ${preview.product_count} 个商品 · ${preview.media_count} 张图片` : '生成隔离预览，不改变线上网站'}</small></div></div><button className="agent-secondary-button agent-full-button" onClick={createPreview} disabled={busy || !approved}>{preview ? '重新生成预览' : '生成发布前预览'}</button>{preview && <><a className="agent-preview-link" href={preview.preview_url} target="_blank" rel="noreferrer">打开预览网站 ↗</a><button className="agent-primary-button agent-full-button" onClick={confirmPreview} disabled={busy || preview.user_confirmed}>{preview.user_confirmed ? '预览已确认' : '确认预览并解锁发布'}</button></>}<div className="agent-release-check"><span className={published || context?.draft.published ? 'check-done' : ''}>4</span><div><strong>发布网站</strong><small>{published ? '已发布到 ' + published.public_url : context?.draft.published ? '已发布，恢复入口已准备好' : preview?.user_confirmed ? '可以发布' : '确认预览后解锁'}</small></div></div><button className="agent-primary-button agent-full-button" onClick={publishDraft} disabled={busy || !preview?.user_confirmed || Boolean(published) || Boolean(context?.draft.published)}>{published || context?.draft.published ? '已发布' : '发布网站'}</button><p className="agent-side-note">发布会先检查预览、批准和安全切换；切换前内容会被保存，之后可以恢复。</p></div>
      </aside></section>
    </div>
  );
}
